import { Input, Text, Textarea, Title } from "@biesse-group/react-components";
import * as Sentry from "@sentry/gatsby";
import React, { FC, useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";

import { useReCaptchaButton } from "../../../hooks/forms/useReCaptchaButton";
import { useRevalidateOnError } from "../../../hooks/forms/useRevalidateOnError";
import { useLabels } from "../../../hooks/useLabels";
import { emailValidationRegex, mobileValidationRegex } from "../../../utils/forms";
import { useLayoutContext } from "../../Layout/layoutContext";
import { RichText } from "../../RichText";
import { CountrySelect } from "../CountrySelect";
import { FormButtons } from "../FormButtons";
import { GdprCheckboxes } from "../GdprCheckboxes";
import { MaterialsForm } from "../MaterialsCheckbox";
import { ModalFormContainer } from "../ModalFormContainer";
import { FormMaterials, FormPayload } from "../models/form-payload";
import {
  formPayloadTitleMap,
  InterestFieldType,
  INTERESTS_FIELD_ITEMS_COMPLETE_VERSION,
  INTERESTS_FIELD_ITEMS_EVENT_VERSION,
} from "../models/interest-field-type";
import { Divider } from "../utils";
import { sendForm } from "../utils/sendForm";
import { InputGrid } from "./InputGrid";
import { ModalOutcome } from "./ModalOutcome";
import { RequestStatus } from "./request-status";

const StyledTextarea = styled(Textarea)`
  display: inline-flex;
  width: 100%;
  height: 100px;
  margin-top: 20px;
  margin-bottom: 10px;
`;

const StyledTextBoxLabel = styled(Text)`
  align-self: flex-end;
  margin-bottom: 20px;
`;

const maxChars = 600;

export interface GenericModalFormProps {
  interestFieldId?: InterestFieldType;
  onPostSuccess?: (payload: FormPayload) => void;
  leadMessage?: string;
  mailTitle?: string;
  title: string;
  description?: string | null;
  className?: string;
  onClose?: (success?: boolean) => void;
  successMessage?: string;
  serviceId?: string;
}

export const GenericModalForm: FC<GenericModalFormProps> = ({
  leadMessage,
  interestFieldId,
  onClose,
  title,
  description,
  successMessage = "",
  onPostSuccess,
  mailTitle,
  serviceId,
  ...props
}) => {
  const [selectedNation, setSelectedNation] = useState<Queries.CountriesStatesCitiesJson>();
  const [requestStatus, setRequestStatus] = useState(RequestStatus.INITIAL);
  const { language } = useLayoutContext();

  const labels = useLabels([
    "info-form-name",
    "info-form-city",
    "info-form-surname",
    "info-form-email",
    "info-form-mobile",
    "info-form-company",
    "info-form-business-role",
    "info-form-materials-title",
    "info-form-back",
    "info-form-send-request-button",
    "info-form-message-placeholder",
    "info-form-available-characters",
  ]);

  const defaultFormValues = useMemo<FormPayload>(
    () => ({
      requestTitle: formPayloadTitleMap[interestFieldId ?? "undefined"],
      requestMessage: leadMessage,
      firstName: "",
      lastName: "",
      company: "",
      businessRole: "",
      country: "",
      email: "",
      locale: language,
      mobile: "",
      materials: [],
      city: "",
      consents: {
        privacy: false,
        marketing: false,
        communication: false,
      },
      service: serviceId,
    }),
    [interestFieldId, leadMessage, serviceId, language]
  );

  const {
    control,
    watch,
    trigger,
    handleSubmit,
    formState: { errors },
  } = useForm<FormPayload>({
    defaultValues: defaultFormValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  useRevalidateOnError(trigger, errors, watch);
  const { handleReCaptchaVerify, isDisabled: isReCaptchaNotReady } = useReCaptchaButton();

  const handlePost = useCallback(
    async (data: FormPayload) => {
      setRequestStatus(RequestStatus.PENDING);
      const token = await handleReCaptchaVerify();

      try {
        await sendForm({
          type:
            interestFieldId === InterestFieldType.BECOME_PROVIDER
              ? "suppliers"
              : interestFieldId === InterestFieldType.SUPPORT_REQUEST
              ? "support"
              : "leads",
          data,
          token,
        });

        setRequestStatus(RequestStatus.SUCCESS);
        onPostSuccess?.(data);
      } catch (e) {
        console.error(e);
        setRequestStatus(RequestStatus.ERROR);
        if (!process.env.EXCLUDE_SENTRY) {
          Sentry.captureException(e);
        }
      }
    },
    [handleReCaptchaVerify, onPostSuccess, interestFieldId]
  );

  const isCompleteVersion =
    !!interestFieldId &&
    [...INTERESTS_FIELD_ITEMS_COMPLETE_VERSION, ...INTERESTS_FIELD_ITEMS_EVENT_VERSION].includes(
      interestFieldId
    );

  // TODO: localize this label if we actually want to show it as an error message
  // (right now it's not shown, inputs in error only have a red border)
  const requiredFieldLabel = "Mandatory field";

  return (
    <ModalFormContainer {...props}>
      {requestStatus !== RequestStatus.INITIAL ? (
        <ModalOutcome {...{ requestStatus, successMessage, onClose }} />
      ) : (
        <>
          <Title color="light" style={{ marginBottom: "10px" }} variant="h3">
            {title}
          </Title>
          <RichText variant="light" raw={description} />
          <InputGrid>
            <Controller
              control={control}
              name="firstName"
              rules={{ required: requiredFieldLabel }}
              render={({ field, fieldState }) => (
                <Input
                  type="text"
                  error={fieldState.error?.message}
                  placeholder={`${labels["info-form-name"]}*`}
                  {...field}
                  shadow="dark"
                />
              )}
            />
            <Controller
              control={control}
              name="lastName"
              rules={{ required: requiredFieldLabel }}
              render={({ field, fieldState }) => (
                <Input
                  type="text"
                  error={fieldState.error?.message}
                  placeholder={`${labels["info-form-surname"]}*`}
                  {...field}
                  shadow="dark"
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              rules={{
                required: requiredFieldLabel,
                pattern: {
                  value: emailValidationRegex,
                  message: "Please enter a valid email",
                },
              }}
              render={({ field, fieldState }) => (
                <Input
                  type="email"
                  error={fieldState.error?.message}
                  placeholder={`${labels["info-form-email"]}*`}
                  shadow="dark"
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name="mobile"
              rules={{
                required: requiredFieldLabel,
                pattern: {
                  value: mobileValidationRegex,
                  message: "Please enter a valid phone number",
                },
              }}
              defaultValue=""
              render={({ field, fieldState }) => (
                <Input
                  type="tel"
                  placeholder={`${labels["info-form-mobile"]}*`}
                  error={fieldState.error?.message}
                  shadow="dark"
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name="country"
              rules={{ required: requiredFieldLabel }}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <CountrySelect
                  value={selectedNation}
                  error={error?.message}
                  onChange={(country) => {
                    setSelectedNation(country);
                    onChange(country.iso3 || "");
                  }}
                />
              )}
            />

            <Controller
              control={control}
              name="city"
              rules={{ required: requiredFieldLabel }}
              render={({ field, fieldState }) => (
                <Input
                  type="text"
                  aria-label="city"
                  error={fieldState.error?.message}
                  style={{ marginBottom: 25 }}
                  placeholder={`${labels["info-form-city"]}*`}
                  {...field}
                  shadow="dark"
                />
              )}
            />

            <div>
              <Controller
                control={control}
                name="company"
                rules={{ required: requiredFieldLabel }}
                render={({ field, fieldState }) => (
                  <Input
                    type="text"
                    aria-label="company"
                    error={fieldState.error?.message}
                    placeholder={`${labels["info-form-company"]}*`}
                    {...field}
                    shadow="dark"
                  />
                )}
              />
            </div>

            {(interestFieldId === InterestFieldType.SUPPORT_REQUEST ||
              interestFieldId === InterestFieldType.BECOME_PROVIDER) && (
              <div>
                <Controller
                  control={control}
                  name="businessRole"
                  rules={{ required: requiredFieldLabel }}
                  render={({ field, fieldState }) => (
                    <Input
                      type="text"
                      aria-label="businessRole"
                      error={fieldState.error?.message}
                      placeholder={`${labels["info-form-business-role"]}*`}
                      {...field}
                      shadow="dark"
                    />
                  )}
                />
              </div>
            )}
          </InputGrid>
          {isCompleteVersion && <Text color="light">{labels["info-form-materials-title"]}</Text>}
          {isCompleteVersion && (
            <Controller
              control={control}
              name="materials"
              rules={{ required: requiredFieldLabel }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <MaterialsForm
                  error={error?.message}
                  onCheckChange={(id, checked) => {
                    const nameAsEnum = id as FormMaterials;
                    const newValue = value ?? [];
                    onChange(
                      checked ? [...newValue, nameAsEnum] : newValue.filter((x) => x !== nameAsEnum)
                    );
                  }}
                />
              )}
            />
          )}
          {interestFieldId === InterestFieldType.SUPPORT_REQUEST && (
            <Controller
              control={control}
              name="requestMessage"
              render={({ field: { value, onChange } }) => (
                <>
                  <StyledTextarea
                    value={value}
                    maxLength={maxChars}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    shadow="dark"
                    placeholder={labels["info-form-message-placeholder"] || ""}
                  />
                  <StyledTextBoxLabel italic size="sm" color="light">
                    {`${maxChars - (value?.length ?? 0)}/${maxChars} ${
                      labels["info-form-available-characters"]
                    }`}
                  </StyledTextBoxLabel>
                </>
              )}
            />
          )}
          <Divider />
          <GdprCheckboxes consentsFieldName="consents" control={control} />

          <FormButtons
            cancelButton={{
              label: labels["info-form-back"] || "",
              onClick: onClose,
            }}
            confirmButton={{
              label: labels["info-form-send-request-button"] || "",
              disabled: isReCaptchaNotReady,
              onClick: handleSubmit(handlePost),
            }}
          />
        </>
      )}
    </ModalFormContainer>
  );
};
